import type { UserExecutionFeedSnapshot } from "@repo/execution-engine-client";
import type { ExecutionHandle, ExecutionSummary } from "../types";

/**
 * A whole-operation frame from the user-execution-feed Durable Object. This is
 * the only realtime channel that covers execution-engine (worker) operations —
 * the tRPC executions stream is Redis-backed and only the legacy engine
 * publishes to it — and the only channel that delivers the reconciliation
 * cron's late completions to an idle page. Its `refs` carry the full display
 * sub-objects the platform-api schema strips.
 */
export type ExecutionFeedOperation =
  UserExecutionFeedSnapshot["operations"][number];

/**
 * Trigger provenance, NOT executor identity — "manual" means a person clicked
 * something. Only manual operations are eligible for feed auto-discovery;
 * scheduled/event/agent operations stay in the notification dock.
 */
export function getFeedOperationExecutionSource(
  operation: ExecutionFeedOperation,
): NonNullable<ExecutionFeedOperation["refs"]["executionSource"]> | null {
  return operation.refs.executionSource ?? null;
}

/**
 * Maps a feed frame into the tRPC-shaped handle the tracking records store.
 * Mirrors mapExecutionEngineOperationToExecutionHandle (the rebalance
 * precedent) so both engine-client entry points produce the same shape.
 */
export function mapFeedOperationToHandle(
  operation: ExecutionFeedOperation,
): ExecutionHandle {
  const workflowId = operation.refs.workflowId ?? undefined;
  const requestKey = operation.refs.requestKey ?? undefined;

  return {
    operationId: operation.operationId,
    portfolioId: operation.portfolioId,
    kind: operation.kind,
    state: operation.state,
    createdAt: operation.createdAt,
    updatedAt: operation.updatedAt,
    finishedAt: operation.finishedAt ?? null,
    refs: {
      ...(workflowId ? { workflowId } : {}),
      runId: operation.runId,
      ...(requestKey ? { requestKey } : {}),
    },
    progress: {
      ...(operation.currentPhase ? { phase: operation.currentPhase } : {}),
      stepIndex: operation.currentStep ?? undefined,
      totalSteps: operation.totalSteps ?? undefined,
    },
    error: null,
    // No nextSteps: the tRPC stream descriptor would point consumers at the
    // legacy Redis-backed SSE, which is silent for exactly the worker ops the
    // feed carries; the feed itself (plus detail polling) is this record's
    // transport.
  };
}

export function mapFeedOperationToSummary(
  operation: ExecutionFeedOperation,
): ExecutionSummary {
  return {
    detail: operation.currentDetail ?? null,
    headline: operation.currentHeadline ?? operation.title,
    phase: operation.currentPhase ?? null,
    stepIndex: operation.currentStep ?? undefined,
    terminalReason: null,
    totalSteps: operation.totalSteps ?? undefined,
  };
}

export const FEED_DISCOVERY_FRESHNESS_MS = 15 * 60 * 1000;

/**
 * Whether an untracked feed operation may auto-surface as a toast: explicitly
 * user-triggered, still in flight, and fresh. Absence of executionSource is
 * NOT user-initiated: legacy-engine automated legs (rebalance bridges,
 * batch/ondo_direct/solana swaps) stamp nothing, and the compat routes fall
 * back to the portfolio OWNER as initiatorUserId — so unstamped background
 * ops do reach the user feed and must not toast. Worker-native creates
 * (market orders, deposit intents, direct withdrawals) become discoverable
 * when the EE user routes stamp them "manual". Terminal snapshot residents
 * never auto-surface; terminal transitions of ALREADY-tracked records are
 * handled by the merge path, not discovery.
 */
export function isFeedOperationDiscoverable(
  operation: ExecutionFeedOperation,
  nowMs: number,
): boolean {
  if (getFeedOperationExecutionSource(operation) !== "manual") {
    return false;
  }

  if (
    operation.state === "completed" ||
    operation.state === "failed" ||
    operation.state === "cancelled"
  ) {
    return false;
  }

  const createdAtMs = Date.parse(operation.createdAt);
  return (
    Number.isFinite(createdAtMs) &&
    nowMs - createdAtMs <= FEED_DISCOVERY_FRESHNESS_MS
  );
}
